import React, { useState, useEffect } from 'react';
import { Clock, GitBranch, Check, X, Plus, Minus, ChevronRight, ChevronDown } from 'lucide-react';
import versionService from '../../../services/versionService';

const VersionHistoryTimeline = ({ projectId, onRestore }) => {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedVersions, setSelectedVersions] = useState([]);
  const [comparison, setComparison] = useState(null);
  const [expandedVersion, setExpandedVersion] = useState(null);
  const [restoring, setRestoring] = useState(false);

  useEffect(() => {
    if (projectId) {
      loadVersions();
    }
  }, [projectId]);

  const loadVersions = async () => {
    try {
      setLoading(true);
      const data = await versionService?.getAll(projectId);
      setVersions(data);
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVersionSelect = (versionId) => {
    setSelectedVersions(prev => {
      if (prev?.includes(versionId)) {
        return prev?.filter(id => id !== versionId);
      }
      if (prev?.length >= 2) {
        return [prev?.[1], versionId];
      }
      return [...prev, versionId];
    });
  };

  const handleCompare = async () => {
    if (selectedVersions?.length !== 2) return;

    try {
      setLoading(true);
      const result = await versionService?.compare(selectedVersions?.[0], selectedVersions?.[1]);
      setComparison(result);
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async (versionId) => {
    try {
      setRestoring(true);
      await versionService?.restore(versionId);
      await loadVersions();
      if (onRestore) {
        onRestore();
      }
    } catch (err) {
      setError(err?.message);
    } finally {
      setRestoring(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const toggleExpand = (versionId) => {
    setExpandedVersion(prev => prev === versionId ? null : versionId);
  };

  if (loading && versions?.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800 text-sm">{error}</p>
      </div>
    );
  }

  if (versions?.length === 0) {
    return (
      <div className="text-center py-12">
        <GitBranch className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-4 text-lg font-medium text-gray-900">No Version History</h3>
        <p className="mt-2 text-sm text-gray-500">Versions will appear here as you save changes to your project.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Compare Controls */}
      {selectedVersions?.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <GitBranch className="h-5 w-5 text-blue-600" />
            <span className="text-sm font-medium text-blue-900">
              {selectedVersions?.length === 1 ? '1 version selected' : `${selectedVersions?.length} versions selected`}
            </span>
          </div>
          <div className="flex gap-2">
            {selectedVersions?.length === 2 && (
              <button
                onClick={handleCompare}
                className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
              >
                Compare Versions
              </button>
            )}
            <button
              onClick={() => setSelectedVersions([])}
              className="px-4 py-2 bg-white text-gray-700 text-sm font-medium rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors"
            >
              Clear Selection
            </button>
          </div>
        </div>
      )}

      {/* Comparison Results */}
      {comparison && (
        <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">Version Comparison</h3>
            <button
              onClick={() => setComparison(null)}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">
                Version {comparison?.version1?.versionNumber}: {comparison?.version1?.versionName}
              </h4>
              <div className="space-y-1 text-sm text-gray-600">
                <p>Files: {comparison?.version1?.fileCount}</p>
                <p>Size: {comparison?.version1?.totalSize}</p>
              </div>
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">
                Version {comparison?.version2?.versionNumber}: {comparison?.version2?.versionName}
              </h4>
              <div className="space-y-1 text-sm text-gray-600">
                <p>Files: {comparison?.version2?.fileCount}</p>
                <p>Size: {comparison?.version2?.totalSize}</p>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-200 pt-4 space-y-3">
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Changes</h4>
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-gray-600">Files:</span>
                  <span className={comparison?.differences?.fileCountDiff > 0 ? 'text-green-600' : comparison?.differences?.fileCountDiff < 0 ? 'text-red-600' : 'text-gray-600'}>
                    {comparison?.differences?.fileCountDiff > 0 ? '+' : ''}{comparison?.differences?.fileCountDiff}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-gray-600">Size:</span>
                  <span className="text-gray-900">{comparison?.differences?.sizeDiff}</span>
                </div>
              </div>
            </div>

            {comparison?.differences?.techStackDiff?.added?.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-green-700 mb-2 flex items-center gap-1">
                  <Plus className="h-4 w-4" /> Added Technologies
                </h4>
                <div className="flex flex-wrap gap-2">
                  {comparison?.differences?.techStackDiff?.added?.map((tech, idx) => (
                    <span key={idx} className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded">
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {comparison?.differences?.techStackDiff?.removed?.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-red-700 mb-2 flex items-center gap-1">
                  <Minus className="h-4 w-4" /> Removed Technologies
                </h4>
                <div className="flex flex-wrap gap-2">
                  {comparison?.differences?.techStackDiff?.removed?.map((tech, idx) => (
                    <span key={idx} className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded">
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {comparison?.differences?.featuresDiff?.added?.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-green-700 mb-2 flex items-center gap-1">
                  <Plus className="h-4 w-4" /> Added Features
                </h4>
                <ul className="space-y-1">
                  {comparison?.differences?.featuresDiff?.added?.map((feature, idx) => (
                    <li key={idx} className="text-sm text-gray-700">• {feature}</li>
                  ))}
                </ul>
              </div>
            )}

            {comparison?.differences?.featuresDiff?.removed?.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-red-700 mb-2 flex items-center gap-1">
                  <Minus className="h-4 w-4" /> Removed Features
                </h4>
                <ul className="space-y-1">
                  {comparison?.differences?.featuresDiff?.removed?.map((feature, idx) => (
                    <li key={idx} className="text-sm text-gray-700">• {feature}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Timeline */}
      <div className="relative">
        <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gray-200"></div>
        
        <div className="space-y-6">
          {versions?.map((version, index) => (
            <div key={version?.id} className="relative flex gap-4">
              {/* Timeline Dot */}
              <div className="relative z-10 flex-shrink-0">
                <div className={`w-16 h-16 rounded-full flex items-center justify-center ${
                  version?.isCurrent ? 'bg-blue-600' : 'bg-gray-300'
                }`}>
                  {version?.isCurrent ? (
                    <Check className="h-8 w-8 text-white" />
                  ) : (
                    <span className="text-white font-semibold text-lg">{version?.versionNumber}</span>
                  )}
                </div>
              </div>

              {/* Version Card */}
              <div className="flex-1 bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow">
                <div className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {version?.versionName || `Version ${version?.versionNumber}`}
                        </h3>
                        {version?.isCurrent && (
                          <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded">
                            Current
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 mb-2">{version?.description}</p>
                      <div className="flex items-center gap-4 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {formatDate(version?.createdAt)}
                        </span>
                        <span>{version?.fileCount} files</span>
                        <span>{version?.totalSize}</span>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={selectedVersions?.includes(version?.id)}
                        onChange={() => handleVersionSelect(version?.id)}
                        className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                      />
                      <button
                        onClick={() => toggleExpand(version?.id)}
                        className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                      >
                        {expandedVersion === version?.id ? (
                          <ChevronDown className="h-5 w-5" />
                        ) : (
                          <ChevronRight className="h-5 w-5" />
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {expandedVersion === version?.id && (
                    <div className="mt-4 pt-4 border-t border-gray-200 space-y-3">
                      {version?.snapshotData?.tech_stack && (
                        <div>
                          <h4 className="text-sm font-medium text-gray-700 mb-2">Tech Stack</h4>
                          <div className="flex flex-wrap gap-2">
                            {version?.snapshotData?.tech_stack?.map((tech, idx) => (
                              <span key={idx} className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                                {tech}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {version?.snapshotData?.features && (
                        <div>
                          <h4 className="text-sm font-medium text-gray-700 mb-2">Features</h4>
                          <ul className="space-y-1">
                            {version?.snapshotData?.features?.map((feature, idx) => (
                              <li key={idx} className="text-sm text-gray-600">• {feature}</li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {!version?.isCurrent && (
                        <button
                          onClick={() => handleRestore(version?.id)}
                          disabled={restoring}
                          className="w-full mt-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                        >
                          {restoring ? 'Restoring...' : 'Restore This Version'}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default VersionHistoryTimeline;